import { Typography } from '@material-ui/core'

import { isolate } from '../../../../helpers/bidi'
import { formatPromotionMinorAmount } from '../../../../helpers/promotionAmount'
import { getTranslate } from '../../../../localization'
import { AppliedPromotion } from '../../../../types/cart'
import useStyles from './styles/index.styles'

type PropertyDiscountsProps = {
  /** Undefined while the first evaluation is in flight; [] means none apply. */
  applied?: AppliedPromotion[]
  /**
   * The lookup failed. Distinct from `applied === undefined`, which is "not yet".
   *
   * Without this the two are the same on screen — nothing — and they mean opposite things
   * to an operator deciding what to tell a guest. The same distinction `useAdvertisedOffers`
   * already draws for the offers list; this component shipped without it because its data
   * shared another hook's error flag, which was gated on a code having been checked and so
   * could never be true here.
   */
  failed?: boolean
}

/**
 * What the property is already giving this booking. STATUS, not a control.
 *
 * This group replaces the "Apply the best available offer automatically" checkbox, and the
 * replacement is the point rather than a side effect. An auto-applied promotion is a pricing
 * rule the property committed to: the operator has no information with which to decide
 * whether this guest should receive it and no authority to decline it on the guest's behalf.
 * A control whose only correct setting is "on" is not a decision, and this one was worse
 * than useless — ticking it disabled the offers list, which is the only place the other kind
 * of discount can be applied from. Two QA rows, one screen.
 *
 * The figures come from `evaluatePromotion`, which returned them all along; the old section
 * filtered the response down to CODE_ENTRY and threw the rest away.
 */
const PropertyDiscounts = ({
  applied,
  failed = false
}: PropertyDiscountsProps) => {
  const classes = useStyles()

  if (failed) {
    return (
      <div className={classes.group}>
        <Typography variant="body2" className={classes.groupLabel}>
          {getTranslate('Applied by the property')}
        </Typography>
        <Typography variant="body2" className={classes.blocker}>
          {getTranslate(
            'Could not check for automatic discounts. Any that apply will still be applied to the booking.'
          )}
        </Typography>
      </div>
    )
  }

  // Nothing known yet. Deliberately renders nothing rather than "none apply" — claiming an
  // absence before the answer arrives is the same error as hiding one.
  if (applied === undefined) {
    return null
  }

  return (
    <div className={classes.group}>
      <Typography variant="body2" className={classes.groupLabel}>
        {getTranslate('Applied by the property')}
      </Typography>

      {applied.length === 0 ? (
        /*
         * Absence is STATED, never implied by an empty space.
         *
         * The whole failure this redesign addresses is an operator inferring that an unseen
         * automatic discount was in play. Rendering nothing here would reproduce that
         * quietly: the operator would have no way to tell "none apply" from "not loaded".
         */
        <Typography variant="body2" className={classes.helper}>
          {getTranslate(
            'No discounts are applied automatically to this booking.'
          )}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" className={classes.helper}>
            {getTranslate('These need no action from you.')}
          </Typography>
          {applied.map((promotion) => (
            <div className={classes.statusRow} key={promotion.promotionId}>
              <Typography variant="body1">{promotion.name}</Typography>
              <Typography variant="body2" className={classes.appliedAmount}>
                {/* isolate(): the minus sign is bidi-neutral, so in Persian an
                    un-isolated "− 1,250,000" can render with the sign on the wrong end,
                    reading as a trailing mark rather than a subtraction. */}
                {isolate(
                  `− ${formatPromotionMinorAmount(
                    promotion.discountMinor,
                    promotion.currency
                  )}`
                )}{' '}
                {getTranslate(promotion.currency)}
              </Typography>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export default PropertyDiscounts
